import { FlaskConical, Radar, Layers, Zap, ArrowUpRight, Target, TrendingUp } from "lucide-react";
import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import FAQ from "@/components/FAQ";
import CTABanner from "@/components/CTABanner";

const IMG = "https://images.unsplash.com/photo-1510519138101-570d1dca3d66?crop=entropy&cs=srgb&fm=jpg&q=85";

const CAPABILITIES = [
  {
    icon: FlaskConical,
    title: "Creative Testing Framework",
    copy: "Structured sprints across hooks, angles and formats. Every asset gets a hypothesis; the algorithm decides who survives.",
  },
  {
    icon: Radar,
    title: "Conversion API (CAPI) Tracking",
    copy: "Server-side event tracking that survives signal loss, so the delivery system learns from real conversions — not probabilities.",
  },
  {
    icon: Layers,
    title: "Dynamic Product Catalog Ads",
    copy: "Full-catalog prospecting and retargeting wired to your feed, with product-level creative logic at scale.",
  },
  {
    icon: Zap,
    title: "Advantage+ Shopping Scaling",
    copy: "Automated placements steered with disciplined guardrails — more budget into what works, faster than competitors can react.",
  },
];

const PHASES = [
  {
    n: "Phase 01",
    title: "Audience Mining",
    copy: "We decompose your customer base into trigger-rich segments and map the highest-probability pockets of demand before a single dollar is spent.",
  },
  {
    n: "Phase 02",
    title: "Creative Velocity",
    copy: "A weekly testing cadence of hooks, angles and formats floods the auction with options. Winners compound; losers are retired without sentiment.",
  },
  {
    n: "Phase 03",
    title: "Media Buying Scaling",
    copy: "Budget increases on evidence only. We exploit scaling windows, manage learning-phase volatility and protect the blended ROAS floor.",
  },
];

const MEASURES = [
  {
    icon: Target,
    title: "Acquisition efficiency",
    copy: "Are we buying customers at a cost the business can sustain — and is that cost moving in the right direction?",
  },
  {
    icon: TrendingUp,
    title: "Revenue contribution",
    copy: "How paid media shows up in the wider business, not only inside platform dashboards.",
  },
  {
    icon: FlaskConical,
    title: "Creative signal quality",
    copy: "Whether the data feeding Meta's delivery decisions is clean enough to trust and act on.",
  },
];

const FAQ_ITEMS = [
  {
    q: "How fast can we launch?",
    a: "Most accounts are rebuilt and live within 14 days: tracking verified in week one, creative pipeline running in week two.",
  },
  {
    q: "Do you require long contracts?",
    a: "No. We work on 90-day engagements — long enough to prove the system, short enough that we have to earn the next quarter.",
  },
  {
    q: "What ad budgets do you work with?",
    a: "We work with businesses that are ready to invest meaningfully in paid media. If the maths of paid scale doesn't work at your current stage, we'll tell you honestly.",
  },
  {
    q: "Who owns the ad accounts and data?",
    a: "You do. Always. Accounts, pixels, audiences and every creative file live in your assets, not ours.",
  },
];

const MetaAds = () => (
  <>
    <PageHero
      eyebrow="Service / 01 — Meta Ads (Facebook & Instagram)"
      lines={[<>The Meta Ads</>, <><span className="text-flame">Command Center.</span></>]}
      sub="Meta rewards advertisers who out-test, out-track and out-scale everyone else. We run the full stack: creative development, server-side measurement and aggressive, evidence-based media buying."
      image={IMG}
      alt="Dark modern agency workspace"
    />

    <section className="py-20 sm:py-28" data-testid="meta-capabilities">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <p className="font-mono text-xs sm:text-sm uppercase tracking-[0.25em] text-flame">Core capabilities</p>
          <h2 className="mt-4 font-syne text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">
            Built for the way Meta actually buys.
          </h2>
        </Reveal>
        <div className="mt-14 grid sm:grid-cols-2 gap-6">
          {CAPABILITIES.map((c, i) => (
            <Reveal key={c.title} delay={0.08 * i} className="h-full">
              <div className="h-full rounded-2xl border border-white/10 bg-surface p-8 transition-colors hover:border-flame/40">
                <div className="h-11 w-11 rounded-xl bg-flame/10 border border-flame/30 flex items-center justify-center">
                  <c.icon className="h-5 w-5 text-flame" />
                </div>
                <h3 className="mt-6 font-syne text-xl font-bold text-white">{c.title}</h3>
                <p className="mt-3 text-sm text-gray-400 leading-relaxed">{c.copy}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="py-20 sm:py-28 border-t border-white/10" data-testid="meta-playbook">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <p className="font-mono text-xs sm:text-sm uppercase tracking-[0.25em] text-flame">Strategy playbook</p>
          <h2 className="mt-4 font-syne text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">
            Three phases. One trajectory.
          </h2>
        </Reveal>
        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {PHASES.map((p, i) => (
            <Reveal key={p.n} delay={0.1 * i} className="h-full">
              <div className="h-full rounded-2xl border border-white/10 bg-surface p-8">
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-flame">{p.n}</p>
                <h3 className="mt-4 font-syne text-xl font-bold text-white">{p.title}</h3>
                <p className="mt-3 text-sm text-gray-400 leading-relaxed">{p.copy}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="py-20 sm:py-28 border-t border-white/10" data-testid="meta-measurement">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="font-mono text-xs sm:text-sm uppercase tracking-[0.25em] text-flame">What we measure</p>
            <h2 className="mt-4 font-syne text-3xl sm:text-4xl font-bold tracking-tight text-white">
              Measured against the business — not the dashboard.
            </h2>
            <p className="mt-5 text-base text-gray-400 leading-relaxed">
              Every Meta account we run is judged on what it contributes to the business: the cost of
              acquired customers, the revenue behind them and the quality of the signals driving delivery.
            </p>
            <Link
              to="/contact"
              data-testid="meta-audit-cta"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-flame px-7 py-3.5 font-semibold text-white transition-all hover:bg-flame-hover"
            >
              Request a Meta audit
              <ArrowUpRight className="h-5 w-5" />
            </Link>
          </Reveal>
        </div>
        <div className="lg:col-span-7">
          <Reveal delay={0.15}>
            <div className="rounded-3xl border border-white/10 bg-surface p-8 sm:p-10">
              <div className="flex items-center justify-between mb-8">
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-gray-400">Measurement pillars</p>
                <p className="font-mono text-xs text-flame">Meta + Instagram</p>
              </div>
              <div className="space-y-7">
                {MEASURES.map((m) => (
                  <div key={m.title} className="flex items-start gap-5">
                    <div className="h-11 w-11 shrink-0 rounded-xl bg-flame/10 border border-flame/30 flex items-center justify-center">
                      <m.icon className="h-5 w-5 text-flame" />
                    </div>
                    <div>
                      <h3 className="font-syne text-lg font-bold text-white">{m.title}</h3>
                      <p className="mt-1.5 text-sm text-gray-400 leading-relaxed">{m.copy}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>

    <section className="py-20 sm:py-28 border-t border-white/10" data-testid="meta-faq">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <p className="font-mono text-xs sm:text-sm uppercase tracking-[0.25em] text-flame">Questions</p>
          <h2 className="mt-4 font-syne text-3xl sm:text-4xl font-bold tracking-tight text-white mb-10">
            Before you scale on Meta.
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <FAQ items={FAQ_ITEMS} testPrefix="meta-faq" />
        </Reveal>
      </div>
    </section>

    <CTABanner
      title="Ready to see what Meta can really do?"
      sub="Book a strategic audit. We'll review your account structure, creative and tracking — and show you the gaps."
      testPrefix="meta-cta"
    />
  </>
);

export default MetaAds;
